import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Settings as SettingsIcon, Search, Users, Crown, Eye, FolderOpen, ChevronDown, ChevronRight, Download, FileSpreadsheet, Clock, LogIn, LogOut, UserCheck, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import PhotoUpload from '@/components/PhotoUpload';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Volunteers = () => {
  const { user, hasPermission } = useContext(AuthContext);
  const { currentMinistry, loading: ministryLoading } = useMinistry();
  const [volunteers, setVolunteers] = useState([]);
  const [adolescents, setAdolescents] = useState([]); // Adolescentes voluntários (com áreas)
  const [allAdolescents, setAllAdolescents] = useState([]); // Todos os adolescentes (para campo filhos)
  const [serviceAreas, setServiceAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [smallGroups, setSmallGroups] = useState([]); // PGs para verificar líderes
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filterNoArea, setFilterNoArea] = useState(false);
  const [filterAreas, setFilterAreas] = useState([]); // Array de IDs de áreas selecionadas
  const [filterPgLeader, setFilterPgLeader] = useState('all'); // all, yes, no, without_pg
  const [filterStatus, setFilterStatus] = useState('all'); // all, ativo, inativo
  
  // Check-in states
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [volunteerCheckins, setVolunteerCheckins] = useState([]);
  const [checkinSearch, setCheckinSearch] = useState('');
  const [checkoutSearch, setCheckoutSearch] = useState('');
  const [loadingCheckins, setLoadingCheckins] = useState(false);
  
  // Dialogs
  const [volunteerDialogOpen, setVolunteerDialogOpen] = useState(false);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [areaDetailDialogOpen, setAreaDetailDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [childrenSearch, setChildrenSearch] = useState(''); // Busca de filhos
  
  // Collapsed categories state
  const [collapsedCategories, setCollapsedCategories] = useState({});
  
  // Area detail state
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaVolunteers, setAreaVolunteers] = useState([]);
  const [areaAdolescents, setAreaAdolescents] = useState([]); // Adolescentes da área
  const [areaLeaderIds, setAreaLeaderIds] = useState([]);
  const [savingLeaders, setSavingLeaders] = useState(false);
  const [areaVolunteerSearch, setAreaVolunteerSearch] = useState('');
  const [editingArea, setEditingArea] = useState(false);
  const [editAreaForm, setEditAreaForm] = useState({ name: '', description: '', category_id: '' });
  
  // Forms
  const [volunteerForm, setVolunteerForm] = useState({
    name: '', cpf: '', phone: '', email: '', birth_date: '', gender: 'masculino',
    photo_url: '', service_area_ids: [], is_pg_leader: false, is_area_leader: false, status: 'ativo',
    children_ids: []
  });
  const [areaForm, setAreaForm] = useState({ name: '', description: '', category_id: '' });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });

  useEffect(() => {
    if (!ministryLoading && currentMinistry?.id) {
      fetchData();
    } else if (!ministryLoading && !currentMinistry) {
      setLoading(false);
    }
  }, [currentMinistry?.id, ministryLoading]);

  const fetchData = async () => {
    if (!currentMinistry?.id) return;
    try {
      const [volunteersRes, areasRes, categoriesRes, adolescentsRes, smallGroupsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/volunteers?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/service-areas`),
        axios.get(`${API}/service-area-categories`),
        axios.get(`${API}/adolescents?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/small-groups?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/events?ministry_id=${currentMinistry.id}`)
      ]);
      setVolunteers(volunteersRes.data);
      setServiceAreas(areasRes.data);
      setCategories(categoriesRes.data);
      setSmallGroups(smallGroupsRes.data);
      // Guarda todos os adolescentes para o campo "filhos"
      setAllAdolescents(adolescentsRes.data);
      // Filtra apenas adolescentes que têm áreas de serviço
      const adolescentsWithAreas = adolescentsRes.data.filter(a => a.service_area_ids && a.service_area_ids.length > 0);
      setAdolescents(adolescentsWithAreas);
      // Ordenar eventos por data (mais recente primeiro)
      const sortedEvents = eventsRes.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(sortedEvents);
      // Selecionar evento mais recente por padrão
      if (sortedEvents.length > 0 && !selectedEventId) {
        setSelectedEventId(sortedEvents[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Fetch check-ins de voluntários para o evento selecionado
  const fetchVolunteerCheckins = async () => {
    if (!selectedEventId) return;
    setLoadingCheckins(true);
    try {
      const response = await axios.get(`${API}/volunteer-checkins?event_id=${selectedEventId}`);
      setVolunteerCheckins(response.data);
    } catch (error) {
      console.error('Erro ao carregar check-ins:', error);
    } finally {
      setLoadingCheckins(false);
    }
  };

  useEffect(() => {
    if (selectedEventId) {
      fetchVolunteerCheckins();
    }
  }, [selectedEventId]);

  // Fazer check-in de voluntário
  const handleVolunteerCheckin = async (volunteerId) => {
    if (!selectedEventId) {
      toast.error('Selecione um evento primeiro');
      return;
    }
    try {
      await axios.post(`${API}/volunteer-checkins`, {
        volunteer_id: volunteerId,
        event_id: selectedEventId,
        ministry_id: currentMinistry?.id
      });
      toast.success('Check-in realizado!');
      fetchVolunteerCheckins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer check-in');
    }
  };

  // Fazer check-out de voluntário
  const handleVolunteerCheckout = async (checkinId) => {
    try {
      await axios.put(`${API}/volunteer-checkins/${checkinId}/checkout`);
      toast.success('Check-out realizado!');
      fetchVolunteerCheckins();
    } catch (error) {
      toast.error('Erro ao fazer check-out');
    }
  };

  // Remover check-in de voluntário
  const handleRemoveCheckin = async (checkinId) => {
    if (!window.confirm('Tem certeza que deseja remover este check-in?')) return;
    try {
      await axios.delete(`${API}/volunteer-checkins/${checkinId}`);
      toast.success('Check-in removido!');
      fetchVolunteerCheckins();
    } catch (error) {
      toast.error('Erro ao remover check-in');
    }
  };

  const handleSaveVolunteer = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/volunteers/${editingId}`, volunteerForm);
        toast.success('Voluntário atualizado!');
      } else {
        await axios.post(`${API}/volunteers?ministry_id=${currentMinistry?.id}`, volunteerForm);
        toast.success('Voluntário cadastrado!');
      }
      setVolunteerDialogOpen(false);
      resetVolunteerForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar voluntário');
    }
  };

  const handleEditVolunteer = (volunteer) => {
    setEditingId(volunteer.id);
    setVolunteerForm({
      name: volunteer.name,
      cpf: volunteer.cpf || '',
      phone: volunteer.phone,
      email: volunteer.email || '',
      birth_date: volunteer.birth_date ? volunteer.birth_date.split('T')[0] : '',
      gender: volunteer.gender || 'masculino',
      photo_url: volunteer.photo_url || '',
      service_area_ids: volunteer.service_area_ids || [],
      is_pg_leader: volunteer.is_pg_leader || false,
      is_area_leader: volunteer.is_area_leader || false,
      status: volunteer.status,
      children_ids: volunteer.children_ids || []
    });
    setVolunteerDialogOpen(true);
  };

  const handleDeleteVolunteer = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este voluntário?')) {
      try {
        await axios.delete(`${API}/volunteers/${id}`);
        toast.success('Voluntário excluído!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao excluir voluntário');
      }
    }
  };

  const handleSaveArea = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/service-areas`, areaForm);
      toast.success('Área criada!');
      setAreaDialogOpen(false);
      setAreaForm({ name: '', description: '', category_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar área');
    }
  };

  const handleDeleteArea = async (id, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Tem certeza que deseja excluir esta área?')) {
      try {
        await axios.delete(`${API}/service-areas/${id}`);
        toast.success('Área excluída!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao excluir área');
      }
    }
  };

  // Category CRUD
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategoryId) {
        await axios.put(`${API}/service-area-categories/${editingCategoryId}`, categoryForm);
        toast.success('Categoria atualizada!');
      } else {
        await axios.post(`${API}/service-area-categories`, categoryForm);
        toast.success('Categoria criada!');
      }
      setCategoryDialogOpen(false);
      setCategoryForm({ name: '', description: '' });
      setEditingCategoryId(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleEditCategory = (category, e) => {
    if (e) e.stopPropagation();
    setEditingCategoryId(category.id);
    setCategoryForm({ name: category.name, description: category.description || '' });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (id, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Tem certeza que deseja excluir esta categoria? Todas as áreas precisam ser movidas ou excluídas antes.')) {
      try {
        await axios.delete(`${API}/service-area-categories/${id}`);
        toast.success('Categoria excluída!');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
      }
    }
  };

  const toggleCategoryCollapse = (categoryId) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Função para abrir detalhes da área
  const handleOpenAreaDetail = async (area) => {
    setSelectedArea(area);
    setAreaLeaderIds(area.leader_ids || []);
    setEditAreaForm({ name: area.name, description: area.description || '', category_id: area.category_id || '' });
    setEditingArea(false);
    setAreaVolunteerSearch('');
    
    // Buscar voluntários 18+ desta área
    const areaVols = volunteers.filter(v => v.service_area_ids?.includes(area.id));
    // Ordenar: líderes primeiro, depois alfabeticamente
    areaVols.sort((a, b) => {
      const aIsLeader = (area.leader_ids || []).includes(a.id);
      const bIsLeader = (area.leader_ids || []).includes(b.id);
      if (aIsLeader && !bIsLeader) return -1;
      if (!aIsLeader && bIsLeader) return 1;
      return a.name.localeCompare(b.name, 'pt-BR');
    });
    setAreaVolunteers(areaVols);
    
    // Buscar adolescentes desta área
    const areaAdols = adolescents.filter(a => a.service_area_ids?.includes(area.id));
    areaAdols.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    setAreaAdolescents(areaAdols);
    
    setAreaDetailDialogOpen(true);
  };

  // Função para toggle líder
  const handleToggleLeader = (volunteerId) => {
    setAreaLeaderIds(prev => 
      prev.includes(volunteerId) 
        ? prev.filter(id => id !== volunteerId)
        : [...prev, volunteerId]
    );
  };

  // Função para salvar líderes
  const handleSaveLeaders = async () => {
    if (!selectedArea) return;
    setSavingLeaders(true);
    try {
      await axios.put(`${API}/service-areas/${selectedArea.id}`, {
        leader_ids: areaLeaderIds
      });
      toast.success('Líderes atualizados!');
      
      // Atualizar a lista de áreas localmente
      setServiceAreas(prev => prev.map(area => 
        area.id === selectedArea.id 
          ? { ...area, leader_ids: areaLeaderIds }
          : area
      ));
      
      // Atualizar selectedArea também
      setSelectedArea(prev => prev ? { ...prev, leader_ids: areaLeaderIds } : null);
    } catch (error) {
      toast.error('Erro ao salvar líderes');
    } finally {
      setSavingLeaders(false);
    }
  };

  // Função para salvar edição da área
  const handleSaveAreaEdit = async () => {
    if (!selectedArea || !editAreaForm.name.trim()) {
      toast.error('Nome da área é obrigatório');
      return;
    }
    
    try {
      await axios.put(`${API}/service-areas/${selectedArea.id}`, {
        name: editAreaForm.name.trim(),
        description: editAreaForm.description?.trim() || null,
        category_id: editAreaForm.category_id || null
      });
      toast.success('Área atualizada!');
      
      // Atualizar localmente
      const updatedArea = { 
        ...selectedArea, 
        name: editAreaForm.name.trim(), 
        description: editAreaForm.description?.trim() || null,
        category_id: editAreaForm.category_id || null
      };
      setSelectedArea(updatedArea);
      setServiceAreas(prev => prev.map(area => 
        area.id === selectedArea.id ? updatedArea : area
      ));
      setEditingArea(false);
    } catch (error) {
      toast.error('Erro ao atualizar área');
    }
  };

  // Filtrar voluntários da área pela busca
  const filteredAreaVolunteers = areaVolunteers.filter(v =>
    v.name.toLowerCase().includes(areaVolunteerSearch.toLowerCase()) ||
    (v.phone && v.phone.includes(areaVolunteerSearch))
  );

  // Organizar áreas por categoria
  const getAreasGroupedByCategory = () => {
    const grouped = {};
    
    // Primeiro, agrupar por categoria
    categories.forEach(cat => {
      grouped[cat.id] = {
        category: cat,
        areas: serviceAreas.filter(area => area.category_id === cat.id)
      };
    });
    
    // Adicionar áreas sem categoria
    const uncategorized = serviceAreas.filter(area => !area.category_id);
    if (uncategorized.length > 0) {
      grouped['uncategorized'] = {
        category: { id: 'uncategorized', name: 'Sem Categoria', description: '' },
        areas: uncategorized
      };
    }
    
    return grouped;
  };

  const resetVolunteerForm = () => {
    setEditingId(null);
    setChildrenSearch(''); // Limpar busca de filhos
    setVolunteerForm({
      name: '', cpf: '', phone: '', email: '', birth_date: '', gender: 'masculino',
      photo_url: '', service_area_ids: [], is_pg_leader: false, is_area_leader: false, status: 'ativo',
      children_ids: []
    });
  };

  // Identificar líderes de PG que têm PG atribuído
  const leadersWithPg = new Set();
  smallGroups.forEach(pg => {
    // Verificar leader_ids (array) - formato atual
    if (pg.leader_ids && Array.isArray(pg.leader_ids)) {
      pg.leader_ids.forEach(id => leadersWithPg.add(id));
    }
    // Verificar leader_id e co_leader_id (formato antigo, caso exista)
    if (pg.leader_id) leadersWithPg.add(pg.leader_id);
    if (pg.co_leader_id) leadersWithPg.add(pg.co_leader_id);
  });

  // Filtrar voluntários com todos os filtros aplicados
  const filteredVolunteers = volunteers
    .filter(v =>
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      (v.email && v.email.toLowerCase().includes(search.toLowerCase())) ||
      (v.phone && v.phone.includes(search))
    )
    .filter(v => !filterNoArea || !v.service_area_ids || v.service_area_ids.length === 0)
    .filter(v => {
      if (filterAreas.length === 0) return true;
      return v.service_area_ids && v.service_area_ids.some(id => filterAreas.includes(id));
    })
    .filter(v => {
      if (filterPgLeader === 'all') return true;
      if (filterPgLeader === 'yes') return v.is_pg_leader;
      if (filterPgLeader === 'no') return !v.is_pg_leader;
      if (filterPgLeader === 'without_pg') return v.is_pg_leader && !leadersWithPg.has(v.id);
      return true;
    })
    .filter(v => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'outros') return !['ativo', 'inativo'].includes(v.status);
      return v.status === filterStatus;
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  // Contagem de voluntários sem área
  const volunteersWithoutArea = volunteers.filter(v => !v.service_area_ids || v.service_area_ids.length === 0);
  
  // Contagem de líderes de PG
  const pgLeaders = volunteers.filter(v => v.is_pg_leader);
  const pgLeadersWithoutPg = pgLeaders.filter(v => !leadersWithPg.has(v.id));
  
  // Contagem por status
  const activeVolunteers = volunteers.filter(v => v.status === 'ativo');
  const inactiveVolunteers = volunteers.filter(v => v.status === 'inativo');
  
  // Toggle área no filtro
  const toggleAreaFilter = (areaId) => {
    setFilterAreas(prev => 
      prev.includes(areaId) 
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };
  
  // Verificar se há filtros ativos
  const hasActiveFilters = filterNoArea || filterAreas.length > 0 || filterPgLeader !== 'all' || filterStatus !== 'all';
  
  // Limpar todos os filtros
  const clearAllFilters = () => {
    setFilterNoArea(false);
    setFilterAreas([]);
    setFilterPgLeader('all');
    setFilterStatus('all');
    setSearch('');
  };

  // Função para exportar voluntários para CSV
  const exportToCSV = () => {
    if (filteredVolunteers.length === 0) {
      toast.error('Nenhum voluntário para exportar');
      return;
    }

    const headers = ['Nome', 'Email', 'Telefone', 'CPF', 'Data Nascimento', 'Idade', 'Gênero', 'Status', 'Líder de PG', 'Líder de Área', 'Papel no PG', 'Áreas de Serviço'];
    
    const rows = filteredVolunteers.map(v => {
      const areas = (v.service_area_ids || [])
        .map(id => serviceAreas.find(a => a.id === id)?.name || '')
        .filter(Boolean)
        .join(', ');
      
      const pgRoles = (v.pg_roles || [])
        .map(pr => `${pr.role}: ${pr.pg_name}`)
        .join(', ');
      
      return [
        v.name || '',
        v.email || '',
        v.phone || '',
        v.cpf || '',
        v.birth_date || '',
        v.age || '',
        v.gender === 'masculino' ? 'Masculino' : v.gender === 'feminino' ? 'Feminino' : '',
        v.status === 'ativo' ? 'Ativo' : v.status === 'inativo' ? 'Inativo' : '',
        v.is_pg_leader ? 'Sim' : 'Não',
        v.is_area_leader ? 'Sim' : 'Não',
        pgRoles,
        areas
      ];
    });

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `voluntarios_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${filteredVolunteers.length} voluntário(s) exportado(s) para CSV`);
  };

  // Função para exportar voluntários para Excel
  const exportToExcel = () => {
    if (filteredVolunteers.length === 0) {
      toast.error('Nenhum voluntário para exportar');
      return;
    }

    let tableHTML = `
      <table>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Telefone</th>
            <th>CPF</th>
            <th>Data Nascimento</th>
            <th>Idade</th>
            <th>Gênero</th>
            <th>Status</th>
            <th>Líder de PG</th>
            <th>Líder de Área</th>
            <th>Papel no PG</th>
            <th>Áreas de Serviço</th>
          </tr>
        </thead>
        <tbody>
    `;

    filteredVolunteers.forEach(v => {
      const areas = (v.service_area_ids || [])
        .map(id => serviceAreas.find(a => a.id === id)?.name || '')
        .filter(Boolean)
        .join(', ');
      
      const pgRoles = (v.pg_roles || [])
        .map(pr => `${pr.role}: ${pr.pg_name}`)
        .join(', ');
      
      tableHTML += `
        <tr>
          <td>${v.name || ''}</td>
          <td>${v.email || ''}</td>
          <td>${v.phone || ''}</td>
          <td>${v.cpf || ''}</td>
          <td>${v.birth_date || ''}</td>
          <td>${v.age || ''}</td>
          <td>${v.gender === 'masculino' ? 'Masculino' : v.gender === 'feminino' ? 'Feminino' : ''}</td>
          <td>${v.status === 'ativo' ? 'Ativo' : v.status === 'inativo' ? 'Inativo' : ''}</td>
          <td>${v.is_pg_leader ? 'Sim' : 'Não'}</td>
          <td>${v.is_area_leader ? 'Sim' : 'Não'}</td>
          <td>${pgRoles}</td>
          <td>${areas}</td>
        </tr>
      `;
    });

    tableHTML += '</tbody></table>';

    const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `voluntarios_${new Date().toISOString().split('T')[0]}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${filteredVolunteers.length} voluntário(s) exportado(s) para Excel`);
  };

  return (
    <div className="fade-in" data-testid="volunteers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>Voluntariado</h1>
          <p className="text-lg text-gray-600">Gerencie equipe e áreas de serviço</p>
        </div>
      </div>

      {/* Verificar permissões para mostrar abas */}
      {(() => {
        const canViewVolunteers = hasPermission('volunteers.create') || hasPermission('volunteers.edit');
        const canViewAreas = hasPermission('service_areas.view') || hasPermission('volunteers.edit');
        const defaultTab = canViewVolunteers ? "volunteers" : "checkin";
        
        return (
          <Tabs defaultValue={defaultTab}>
            <TabsList>
              {canViewVolunteers && (
                <TabsTrigger value="volunteers">Voluntários 18+ ({volunteers.length})</TabsTrigger>
              )}
              <TabsTrigger value="checkin">
                <Clock size={16} className="mr-1" />
                Check-in ({volunteerCheckins.length})
              </TabsTrigger>
              {canViewAreas && (
                <TabsTrigger value="areas">Áreas de Serviço ({serviceAreas.length})</TabsTrigger>
              )}
            </TabsList>

            {canViewVolunteers && (
              <TabsContent value="volunteers">
          {/* Barra de Filtros */}
          <div className="card mb-6 p-3 sm:p-4 overflow-x-auto">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              {/* Busca */}
              <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar voluntário..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9 w-full"
                  data-testid="search-volunteer"
                />
              </div>
              
              {/* Filtro por Área (Multi-select) */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`h-9 w-full sm:w-auto sm:min-w-[180px] justify-between ${filterAreas.length > 0 ? 'border-purple-500 bg-purple-50' : ''}`}
                    data-testid="filter-area"
                  >
                    <span className="truncate">
                      {filterAreas.length === 0 
                        ? 'Áreas de Serviço' 
                        : filterAreas.length === 1 
                          ? serviceAreas.find(a => a.id === filterAreas[0])?.name || '1 área'
                          : `${filterAreas.length} áreas`}
                    </span>
                    <ChevronDown size={16} className="ml-2 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Áreas de Serviço</span>
                      {filterAreas.length > 0 && (
                        <button 
                          onClick={() => setFilterAreas([])}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Limpar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto p-2">
                    {serviceAreas.map(area => {
                      const count = volunteers.filter(v => v.service_area_ids?.includes(area.id)).length;
                      return (
                        <label 
                          key={area.id} 
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer"
                        >
                          <Checkbox 
                            checked={filterAreas.includes(area.id)}
                            onCheckedChange={() => toggleAreaFilter(area.id)}
                          />
                          <span className="text-sm flex-1 truncate">{area.name}</span>
                          <span className="text-xs text-gray-400">{count}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Filtro Líder de PG */}
              <Select value={filterPgLeader} onValueChange={setFilterPgLeader}>
                <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="filter-pg-leader">
                  <SelectValue placeholder="Líder de PG" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">✅ Líderes de PG ({pgLeaders.length})</SelectItem>
                  <SelectItem value="without_pg">⚠️ Líderes sem PG ({pgLeadersWithoutPg.length})</SelectItem>
                  <SelectItem value="no">Não é líder</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Filtro Status */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Status</SelectItem>
                  <SelectItem value="ativo">🟢 Ativos ({activeVolunteers.length})</SelectItem>
                  <SelectItem value="inativo">🔴 Inativos ({inactiveVolunteers.length})</SelectItem>
                  <SelectItem value="outros">🟡 Em processo ({volunteers.filter(v => !['ativo', 'inativo'].includes(v.status)).length})</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Filtro Sem Área */}
              <Button 
                variant={filterNoArea ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterNoArea(!filterNoArea)}
                className={`h-9 w-full sm:w-auto ${filterNoArea ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                data-testid="filter-no-area-btn"
              >
                ⚠️ Sem Área ({volunteersWithoutArea.length})
              </Button>
              
              {/* Limpar Filtros */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearAllFilters}
                  className="h-9 text-gray-500 hover:text-gray-700"
                >
                  ✕ Limpar
                </Button>
              )}
            </div>
            
            {/* Contador de resultados */}
            <div className="mt-3 pt-3 border-t flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <span className="text-sm text-gray-600">
                Mostrando <strong>{filteredVolunteers.length}</strong> de <strong>{volunteers.length}</strong> voluntários
                {hasActiveFilters && <span className="text-amber-600 ml-2">(filtros ativos)</span>}
              </span>
              
              <div className="flex gap-2 w-full sm:w-auto">
                {/* Botão de Exportação */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 flex-1 sm:flex-none" data-testid="export-volunteers-button">
                      <Download size={16} className="mr-1" />
                      Exportar
                      <ChevronDown size={14} className="ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2">
                    <div className="space-y-1">
                      <button
                        onClick={exportToCSV}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition"
                        data-testid="export-volunteers-csv"
                      >
                        <FileSpreadsheet size={16} className="text-green-600" />
                        Exportar CSV
                      </button>
                      <button
                        onClick={exportToExcel}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition"
                        data-testid="export-volunteers-excel"
                      >
                        <FileSpreadsheet size={16} className="text-blue-600" />
                        Exportar Excel
                      </button>
                    </div>
                    {hasActiveFilters && (
                      <p className="text-xs text-gray-500 mt-2 pt-2 border-t">
                        Exportando {filteredVolunteers.length} de {volunteers.length}
                      </p>
                    )}
                  </PopoverContent>
                </Popover>
                
                <Button onClick={() => { resetVolunteerForm(); setVolunteerDialogOpen(true); }} className="btn-primary h-9 flex-1 sm:flex-none" data-testid="add-volunteer-btn">
                  <Plus size={18} className="mr-1" />
                  <span className="hidden sm:inline">Novo Voluntário</span>
                  <span className="sm:hidden">Novo</span>
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">Carregando...</div>
          ) : filteredVolunteers.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500">Nenhum voluntário encontrado</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredVolunteers.map((volunteer) => (
                <div key={volunteer.id} className={`card ${volunteer.status === 'inativo' ? 'opacity-60 border-red-200' : volunteer.status !== 'ativo' ? 'border-amber-200' : ''}`} data-testid="volunteer-card">
                  <div className="flex items-start space-x-3 sm:space-x-4 mb-4">
                    {volunteer.photo_url ? (
                      <img src={volunteer.photo_url} alt={volunteer.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                        <span className="text-xl sm:text-2xl font-bold text-purple-600">{volunteer.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-base sm:text-lg truncate">{volunteer.name}</h3>
                        {volunteer.status === 'inativo' && (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Inativo</span>
                        )}
                        {volunteer.status && !['ativo', 'inativo'].includes(volunteer.status) && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            {volunteer.status === 'processo_iniciado' ? 'Em processo' : 
                             volunteer.status === 'aguardando_entrevista' ? 'Aguardando' :
                             volunteer.status === 'aguardando_grupo' ? 'Aguardando' :
                             volunteer.status === 'encaminhado_lider' ? 'Encaminhado' : volunteer.status}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{volunteer.email || volunteer.phone}</p>
                      {volunteer.age && <p className="text-sm text-gray-500">{volunteer.age} anos</p>}
                    </div>
                  </div>
                  
                  {/* Tags de Líder de PG e Papel no PG */}
                  {(volunteer.is_pg_leader || (volunteer.pg_roles && volunteer.pg_roles.length > 0)) && (
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {volunteer.is_pg_leader && !volunteer.pg_roles?.length && (
                        <>
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded flex items-center gap-1">
                            <Crown size={12} /> Líder de PG
                          </span>
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                            Sem PG
                          </span>
                        </>
                      )}
                      {volunteer.pg_roles && volunteer.pg_roles.map((pgRole, idx) => (
                        <span 
                          key={idx} 
                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                            pgRole.role === 'Líder' 
                              ? 'bg-amber-100 text-amber-800' 
                              : 'bg-teal-100 text-teal-800'
                          }`}
                        >
                          <Crown size={12} />
                          {pgRole.role}: {pgRole.pg_name}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {volunteer.service_area_ids && volunteer.service_area_ids.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {volunteer.service_area_ids.slice(0, 3).map((areaId) => {
                        const area = serviceAreas.find(a => a.id === areaId);
                        return area ? (
                          <span key={areaId} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {area.name}
                          </span>
                        ) : null;
                      })}
                      {volunteer.service_area_ids.length > 3 && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          +{volunteer.service_area_ids.length - 3}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded flex items-center gap-1">
                        Sem área de serviço
                      </span>
                    </div>
                  )}
                  
                  {/* Filhos vinculados */}
                  {volunteer.children_ids && volunteer.children_ids.length > 0 && (
                    <div className="mb-4 p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-xs font-medium text-blue-700 mb-1.5 flex items-center gap-1">
                        <Users size={12} /> Filhos vinculados ({volunteer.children_ids.length})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {volunteer.children_ids.slice(0, 3).map((childId) => {
                          const child = allAdolescents.find(a => a.id === childId);
                          return child ? (
                            <span key={childId} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              {child.name.split(' ')[0]}
                            </span>
                          ) : null;
                        })}
                        {volunteer.children_ids.length > 3 && (
                          <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded">
                            +{volunteer.children_ids.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex space-x-2">
                    <Button onClick={() => handleEditVolunteer(volunteer)} variant="outline" size="sm" className="flex-1" data-testid="edit-volunteer-btn">
                      <Edit size={16} className="mr-1" />
                      Editar
                    </Button>
                    <Button onClick={() => handleDeleteVolunteer(volunteer.id)} variant="outline" size="sm" data-testid="delete-volunteer-btn">
                      <Trash2 size={16} className="text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
            )}

        {/* Aba de Check-in de Voluntários */}
        <TabsContent value="checkin">
          <div className="card mb-6 p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
              {/* Seletor de Evento */}
              <div className="flex-1">
                <Label className="mb-2 block text-sm font-medium">Selecione o Evento</Label>
                <Select value={selectedEventId || ''} onValueChange={setSelectedEventId}>
                  <SelectTrigger className="w-full" data-testid="checkin-event-select">
                    <SelectValue placeholder="Selecione um evento" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} />
                          <span className="truncate">{event.name} - {format(parseISO(event.date), "dd/MM/yyyy", { locale: ptBR })}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Botão Novo Voluntário */}
              <div className="flex items-end">
                <Button
                  onClick={() => {
                    setEditingId(null);
                    setVolunteerForm({
                      name: '', cpf: '', phone: '', email: '', birth_date: '',
                      service_area_ids: [], is_pg_leader: false, is_area_leader: false, status: 'ativo',
                      ministry_id: currentMinistry?.id || '', photo_url: '', children_ids: []
                    });
                    setVolunteerDialogOpen(true);
                  }}
                  className="btn-primary whitespace-nowrap"
                  data-testid="checkin-new-volunteer-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Novo Voluntário
                </Button>
              </div>
              
              {/* Estatísticas */}
              {selectedEventId && (
                <div className="flex gap-2 sm:gap-4 flex-wrap justify-center sm:justify-start">
                  <div className="text-center px-3 sm:px-4 py-2 bg-green-50 rounded-lg min-w-[70px]">
                    <div className="text-xl sm:text-2xl font-bold text-green-600">
                      {volunteerCheckins.filter(c => c.status === 'checked_in').length}
                    </div>
                    <div className="text-xs text-green-700">Presentes</div>
                  </div>
                  <div className="text-center px-3 sm:px-4 py-2 bg-blue-50 rounded-lg min-w-[70px]">
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">
                      {volunteerCheckins.filter(c => c.status === 'checked_out').length}
                    </div>
                    <div className="text-xs text-blue-700">Saíram</div>
                  </div>
                  <div className="text-center px-3 sm:px-4 py-2 bg-gray-50 rounded-lg min-w-[70px]">
                    <div className="text-xl sm:text-2xl font-bold text-gray-600">
                      {volunteerCheckins.length}
                    </div>
                    <div className="text-xs text-gray-700">Total</div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Busca de voluntário para check-in */}
            {selectedEventId && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar por nome, telefone, email ou CPF..."
                  value={checkinSearch}
                  onChange={(e) => setCheckinSearch(e.target.value)}
                  className="pl-9"
                  data-testid="checkin-search"
                />
              </div>
            )}
          </div>

          {!selectedEventId ? (
            <div className="card text-center py-12">
              <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Selecione um evento para fazer check-in de voluntários</p>
            </div>
          ) : loadingCheckins ? (
            <div className="text-center py-12">Carregando...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Lista de voluntários para check-in */}
              <div className="card">
                <h3 className="font-semibold text-base sm:text-lg mb-4 flex items-center gap-2">
                  <Users size={18} className="sm:w-5 sm:h-5" />
                  Voluntários Disponíveis
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {volunteers
                    .filter(v => v.status === 'ativo')
                    .filter(v => !volunteerCheckins.some(c => c.volunteer_id === v.id))
                    .filter(v => 
                      checkinSearch === '' || 
                      v.name.toLowerCase().includes(checkinSearch.toLowerCase()) ||
                      (v.phone && v.phone.includes(checkinSearch)) ||
                      (v.email && v.email.toLowerCase().includes(checkinSearch.toLowerCase())) ||
                      (v.cpf && v.cpf.includes(checkinSearch))
                    )
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                    .map(volunteer => (
                      <div 
                        key={volunteer.id} 
                        className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition gap-2"
                      >
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          {volunteer.photo_url ? (
                            <img src={volunteer.photo_url} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                              <span className="text-purple-600 font-bold text-sm">{volunteer.name.charAt(0)}</span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">{volunteer.name}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {volunteer.phone || ''}{volunteer.phone && volunteer.service_area_ids?.length ? ' | ' : ''}
                              {volunteer.service_area_ids?.slice(0, 2).map(id => 
                                serviceAreas.find(a => a.id === id)?.name
                              ).filter(Boolean).join(', ') || (!volunteer.phone ? 'Sem área' : '')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 shrink-0 px-2 sm:px-3"
                          onClick={() => handleVolunteerCheckin(volunteer.id)}
                          data-testid={`checkin-btn-${volunteer.id}`}
                        >
                          <LogIn size={16} />
                          <span className="hidden sm:inline ml-1">Check-in</span>
                        </Button>
                      </div>
                    ))}
                  {volunteers.filter(v => v.status === 'ativo' && !volunteerCheckins.some(c => c.volunteer_id === v.id)).length === 0 && checkinSearch === '' && (
                    <p className="text-center text-gray-500 py-4">Todos os voluntários já fizeram check-in</p>
                  )}
                  {checkinSearch !== '' && volunteers
                    .filter(v => v.status === 'ativo')
                    .filter(v => !volunteerCheckins.some(c => c.volunteer_id === v.id))
                    .filter(v => 
                      v.name.toLowerCase().includes(checkinSearch.toLowerCase()) ||
                      (v.phone && v.phone.includes(checkinSearch)) ||
                      (v.email && v.email.toLowerCase().includes(checkinSearch.toLowerCase())) ||
                      (v.cpf && v.cpf.includes(checkinSearch))
                    ).length === 0 && (
                    <p className="text-center text-gray-500 py-4">Nenhum voluntário encontrado para "{checkinSearch}"</p>
                  )}
                </div>
              </div>

              {/* Lista de check-ins realizados */}
              <div className="card">
                <h3 className="font-semibold text-base sm:text-lg mb-4 flex items-center gap-2">
                  <UserCheck size={18} className="sm:w-5 sm:h-5" />
                  Check-ins Realizados ({volunteerCheckins.length})
                </h3>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    placeholder="Buscar por nome, telefone, email ou CPF..."
                    value={checkoutSearch}
                    onChange={(e) => setCheckoutSearch(e.target.value)}
                    className="pl-9 text-sm"
                    data-testid="checkout-search"
                  />
                </div>
                <div className="space-y-2 max-h-[400px] sm:max-h-[500px] overflow-y-auto">
                  {volunteerCheckins
                    .sort((a, b) => new Date(b.checkin_time) - new Date(a.checkin_time))
                    .filter(checkin => {
                      if (checkoutSearch === '') return true;
                      const volunteer = volunteers.find(v => v.id === checkin.volunteer_id);
                      if (!volunteer) return false;
                      const s = checkoutSearch.toLowerCase();
                      return volunteer.name.toLowerCase().includes(s) ||
                        (volunteer.phone && volunteer.phone.includes(checkoutSearch)) ||
                        (volunteer.email && volunteer.email.toLowerCase().includes(s)) ||
                        (volunteer.cpf && volunteer.cpf.includes(checkoutSearch));
                    })
                    .map(checkin => {
                      const volunteer = volunteers.find(v => v.id === checkin.volunteer_id);
                      if (!volunteer) return null;
                      return (
                        <div 
                          key={checkin.id} 
                          className={`flex items-center justify-between p-2 sm:p-3 rounded-lg transition gap-2 ${
                            checkin.status === 'checked_out' ? 'bg-blue-50' : 'bg-green-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            {volunteer.photo_url ? (
                              <img src={volunteer.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-purple-600 font-bold">{volunteer.name.charAt(0)}</span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-sm sm:text-base truncate">{volunteer.name}</p>
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs text-gray-500">
                                <span>
                                  Entrada: {format(parseISO(checkin.checkin_time), "HH:mm", { locale: ptBR })}
                                </span>
                                {checkin.checkout_time && (
                                  <span className="text-blue-600">
                                    | Saída: {format(parseISO(checkin.checkout_time), "HH:mm", { locale: ptBR })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 sm:gap-2 shrink-0">
                            {checkin.status === 'checked_in' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-blue-500 text-blue-600 hover:bg-blue-50 px-2 sm:px-3"
                                onClick={() => handleVolunteerCheckout(checkin.id)}
                                data-testid={`checkout-btn-${checkin.id}`}
                              >
                                <LogOut size={16} />
                                <span className="hidden sm:inline ml-1">Saída</span>
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50 px-2"
                              onClick={() => handleRemoveCheckin(checkin.id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  {volunteerCheckins.length === 0 && (
                    <p className="text-center text-gray-500 py-4">Nenhum check-in realizado ainda</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {canViewAreas && (
        <TabsContent value="areas">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <p className="text-gray-600">{categories.length} categoria(s) • {serviceAreas.length} área(s)</p>
            {user?.role === 'admin' && (
              <div className="flex gap-2 w-full sm:w-auto">
                <Button onClick={() => { setEditingCategoryId(null); setCategoryForm({ name: '', description: '' }); setCategoryDialogOpen(true); }} variant="outline" data-testid="add-category-btn">
                  <FolderOpen size={18} className="mr-2" />
                  Nova Categoria
                </Button>
                <Button onClick={() => setAreaDialogOpen(true)} className="btn-primary" data-testid="add-area-btn">
                  <Plus size={20} className="mr-2" />
                  Nova Área
                </Button>
              </div>
            )}
          </div>

          {/* Categorias com suas áreas */}
          {loading ? (
            <div className="text-center py-12">Carregando...</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(getAreasGroupedByCategory()).map(([catId, { category, areas }]) => (
                <div key={catId} className="border rounded-lg overflow-hidden">
                  {/* Cabeçalho da categoria */}
                  <div 
                    className={`flex items-center justify-between p-4 cursor-pointer ${
                      catId === 'uncategorized' ? 'bg-gray-100' : 'bg-gradient-to-r from-purple-50 to-purple-100'
                    }`}
                    onClick={() => toggleCategoryCollapse(catId)}
                  >
                    <div className="flex items-center gap-3">
                      {collapsedCategories[catId] ? (
                        <ChevronRight size={20} className="text-gray-500" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-500" />
                      )}
                      <FolderOpen size={20} className={catId === 'uncategorized' ? 'text-gray-500' : 'text-purple-600'} />
                      <div>
                        <h3 className="font-bold text-gray-800">{category.name}</h3>
                        {category.description && <p className="text-sm text-gray-600">{category.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">{areas.length} área(s)</span>
                      {user?.role === 'admin' && catId !== 'uncategorized' && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={(e) => handleEditCategory(category, e)}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => handleDeleteCategory(category.id, e)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Áreas da categoria */}
                  {!collapsedCategories[catId] && areas.length > 0 && (
                    <div className="p-4 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {areas.map((area) => {
                          const areaVols = volunteers.filter(v => v.service_area_ids?.includes(area.id));
                          const areaAdols = adolescents.filter(a => a.service_area_ids?.includes(area.id));
                          const leaders = areaVols.filter(v => (area.leader_ids || []).includes(v.id));
                          const totalVolunteers = areaVols.length + areaAdols.length;
                          
                          return (
                            <div 
                              key={area.id} 
                              className="p-3 border rounded-lg hover:shadow-md transition cursor-pointer bg-white"
                              data-testid="area-card" 
                              onClick={() => handleOpenAreaDetail(area)}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-800">{area.name}</h4>
                                  {area.description && <p className="text-xs text-gray-500">{area.description}</p>}
                                </div>
                                {user?.role === 'admin' && (
                                  <Button 
                                    onClick={(e) => handleDeleteArea(area.id, e)} 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    data-testid="delete-area-btn"
                                  >
                                    <Trash2 size={14} className="text-red-500" />
                                  </Button>
                                )}
                              </div>
                              
                              {leaders.length > 0 && (
                                <div className="mb-2">
                                  <div className="flex flex-wrap gap-1">
                                    {leaders.slice(0, 2).map(leader => (
                                      <span key={leader.id} className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                        <Crown size={10} /> {leader.name.split(' ')[0]}
                                      </span>
                                    ))}
                                    {leaders.length > 2 && (
                                      <span className="text-xs text-amber-700">+{leaders.length - 2}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex justify-between items-center text-xs text-gray-500">
                                <div className="flex items-center gap-2">
                                  <span className="flex items-center gap-1" title="Voluntários 18+">
                                    <span className="bg-blue-100 text-blue-700 px-1 rounded text-[10px]">18+</span> {areaVols.length}
                                  </span>
                                  {areaAdols.length > 0 && (
                                    <span className="flex items-center gap-1" title="Adolescentes">
                                      <span className="bg-green-100 text-green-700 px-1 rounded text-[10px]">Adol</span> {areaAdols.length}
                                    </span>
                                  )}
                                </div>
                                <span className="text-blue-600 flex items-center gap-1">
                                  <Eye size={10} /> Detalhes
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {!collapsedCategories[catId] && areas.length === 0 && (
                    <div className="p-4 bg-white text-center text-gray-500 text-sm">
                      Nenhuma área nesta categoria
                    </div>
                  )}
                </div>
              ))}
              
              {categories.length === 0 && serviceAreas.length === 0 && (
                <div className="card text-center py-12">
                  <FolderOpen size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">Nenhuma categoria ou área cadastrada</p>
                  <p className="text-sm text-gray-400 mt-1">Comece criando uma categoria</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>
            )}
          </Tabs>
        );
      })()}

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={(open) => { setCategoryDialogOpen(open); if (!open) { setEditingCategoryId(null); setCategoryForm({ name: '', description: '' }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategoryId ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCategory} className="space-y-4">
            <div>
              <Label>Nome da Categoria *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                placeholder="Ex: Ministério de Louvor"
                required
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Descrição opcional"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-primary">{editingCategoryId ? 'Salvar' : 'Criar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Area Detail Dialog */}
      <Dialog open={areaDetailDialogOpen} onOpenChange={(open) => { setAreaDetailDialogOpen(open); if (!open) setEditingArea(false); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SettingsIcon size={20} />
                {editingArea ? 'Editar Área' : selectedArea?.name}
              </div>
              {!editingArea && (
                <Button variant="ghost" size="sm" onClick={() => setEditingArea(true)}>
                  <Edit size={16} className="mr-1" />
                  Editar
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedArea && (
            <div className="space-y-4">
              {/* Modo de edição da área */}
              {editingArea ? (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label>Nome da Área *</Label>
                    <Input 
                      value={editAreaForm.name} 
                      onChange={(e) => setEditAreaForm({...editAreaForm, name: e.target.value})}
                      placeholder="Nome da área"
                    />
                  </div>
                  <div>
                    <Label>Descrição</Label>
                    <Input 
                      value={editAreaForm.description} 
                      onChange={(e) => setEditAreaForm({...editAreaForm, description: e.target.value})}
                      placeholder="Descrição da área (opcional)"
                    />
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Select value={editAreaForm.category_id || 'none'} onValueChange={(val) => setEditAreaForm({...editAreaForm, category_id: val === 'none' ? '' : val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditingArea(false)}>Cancelar</Button>
                    <Button onClick={handleSaveAreaEdit} className="btn-primary">Salvar Alterações</Button>
                  </div>
                </div>
              ) : (
                <>
                  {selectedArea.description && (
                    <p className="text-gray-600">{selectedArea.description}</p>
                  )}
                </>
              )}
              
              {!editingArea && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Dica:</strong> Marque a caixa ao lado do nome para definir como líder da área. Você pode ter múltiplos líderes.
                    </p>
                  </div>
                  
                  {/* Seção de Voluntários 18+ */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-bold text-gray-800 flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">18+</span>
                        Voluntários ({areaVolunteers.length})
                      </h4>
                      <div className="text-sm text-amber-700 flex items-center gap-1">
                        <Crown size={14} />
                        {areaLeaderIds.length} líder(es)
                      </div>
                    </div>
                    
                    {/* Campo de busca */}
                    {(areaVolunteers.length > 0 || areaAdolescents.length > 0) && (
                      <div className="mb-4">
                        <div className="relative">
                          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input 
                            placeholder="Buscar voluntário..."
                            value={areaVolunteerSearch}
                            onChange={(e) => setAreaVolunteerSearch(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        {areaVolunteerSearch && (
                          <p className="text-xs text-gray-500 mt-1">
                            Mostrando {filteredAreaVolunteers.length} de {areaVolunteers.length} voluntário(s) 18+
                          </p>
                        )}
                      </div>
                    )}
                
                    {areaVolunteers.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                        <p className="text-sm">Nenhum voluntário 18+ nesta área</p>
                      </div>
                    ) : filteredAreaVolunteers.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <p>Nenhum voluntário encontrado com &ldquo;{areaVolunteerSearch}&rdquo;</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredAreaVolunteers.map(volunteer => {
                          const isLeader = areaLeaderIds.includes(volunteer.id);
                          return (
                            <div 
                              key={volunteer.id} 
                              className={`flex items-center justify-between p-3 rounded-lg border transition ${
                                isLeader ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {volunteer.photo_url ? (
                                  <img src={volunteer.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <Users size={18} className="text-blue-500" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-gray-800 flex items-center gap-2">
                                {volunteer.name}
                                {isLeader && (
                                  <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Crown size={10} /> Líder
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">{volunteer.phone}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={isLeader}
                              onCheckedChange={() => handleToggleLeader(volunteer.id)}
                              data-testid={`leader-checkbox-${volunteer.id}`}
                            />
                            <span className="text-xs text-gray-500">Líder</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                  </div>

                  {/* Seção de Adolescentes Voluntários */}
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-bold text-gray-800 flex items-center gap-2">
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">Adolescentes</span>
                        Voluntários ({areaAdolescents.length})
                      </h4>
                    </div>
                    
                    {areaAdolescents.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                        <p className="text-sm">Nenhum adolescente voluntário nesta área</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {areaAdolescents
                          .filter(a => !areaVolunteerSearch || a.name.toLowerCase().includes(areaVolunteerSearch.toLowerCase()))
                          .map(adolescent => (
                            <div 
                              key={adolescent.id} 
                              className="flex items-center justify-between p-3 rounded-lg border bg-green-50 border-green-200"
                            >
                              <div className="flex items-center gap-3">
                                {adolescent.photo_url ? (
                                  <img src={adolescent.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                    <Users size={18} className="text-green-500" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-semibold text-gray-800">{adolescent.name}</p>
                                  <p className="text-sm text-gray-500">{adolescent.age} anos • {adolescent.phone || 'Sem telefone'}</p>
                                </div>
                              </div>
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                {adolescent.gender === 'masculino' ? '♂' : '♀'} {adolescent.engagement_status}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
              
                  {areaVolunteers.length > 0 && (
                    <div className="flex justify-end pt-4 border-t">
                      <Button 
                        onClick={handleSaveLeaders} 
                        className="btn-primary"
                        disabled={savingLeaders}
                        data-testid="save-leaders-btn"
                      >
                        {savingLeaders ? 'Salvando...' : 'Salvar Líderes'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Volunteer Dialog */}
      <Dialog open={volunteerDialogOpen} onOpenChange={(open) => { setVolunteerDialogOpen(open); if (!open) resetVolunteerForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Voluntário' : 'Novo Voluntário'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveVolunteer} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Foto</Label>
                <PhotoUpload currentPhotoUrl={volunteerForm.photo_url} onPhotoChange={(url) => setVolunteerForm({...volunteerForm, photo_url: url})} />
              </div>
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input value={volunteerForm.name} onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})} required />
              </div>
              <div>
                <Label>CPF</Label>
                <Input value={volunteerForm.cpf} onChange={(e) => setVolunteerForm({...volunteerForm, cpf: e.target.value})} />
              </div>
              <div>
                <Label>Data Nascimento</Label>
                <Input type="date" value={volunteerForm.birth_date} onChange={(e) => setVolunteerForm({...volunteerForm, birth_date: e.target.value})} />
              </div>
              <div>
                <Label>Gênero</Label>
                <Select
                  value={volunteerForm.gender || 'masculino'}
                  onValueChange={(value) => setVolunteerForm({...volunteerForm, gender: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o gênero" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="feminino">Feminino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Telefone *</Label>
                <Input value={volunteerForm.phone} onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})} required />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={volunteerForm.email} onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})} />
              </div>
              
              {/* Status e Líder de PG */}
              <div>
                <Label>Status</Label>
                <Select value={volunteerForm.status || 'ativo'} onValueChange={(value) => setVolunteerForm({...volunteerForm, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">🟢 Ativo</SelectItem>
                    <SelectItem value="inativo">🔴 Inativo</SelectItem>
                    <SelectItem value="processo_iniciado">🟡 Processo Iniciado</SelectItem>
                    <SelectItem value="aguardando_entrevista">🟡 Aguardando Entrevista</SelectItem>
                    <SelectItem value="aguardando_grupo">🟡 Aguardando Grupo</SelectItem>
                    <SelectItem value="encaminhado_lider">🟡 Encaminhado ao Líder</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Apenas voluntários Ativos aparecem nas listas de seleção</p>
              </div>
              <div className="flex items-center space-x-4 pt-6">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="is_pg_leader" 
                    checked={volunteerForm.is_pg_leader} 
                    onCheckedChange={(checked) => setVolunteerForm({...volunteerForm, is_pg_leader: checked})}
                  />
                  <Label htmlFor="is_pg_leader" className="cursor-pointer">Líder de PG</Label>
                </div>
              </div>
              
              <div className="col-span-2">
                <Label>Áreas de Serviço</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto border rounded p-2">
                  {serviceAreas.map((area) => (
                    <div key={area.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`area-${area.id}`}
                        checked={volunteerForm.service_area_ids.includes(area.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setVolunteerForm({...volunteerForm, service_area_ids: [...volunteerForm.service_area_ids, area.id]});
                          } else {
                            setVolunteerForm({...volunteerForm, service_area_ids: volunteerForm.service_area_ids.filter(id => id !== area.id)});
                          }
                        }}
                      />
                      <Label htmlFor={`area-${area.id}`} className="cursor-pointer text-sm">{area.name}</Label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Campo Filhos - Vincular a adolescentes */}
              <div className="col-span-2">
                <Label className="flex items-center gap-2">
                  <Users size={16} />
                  Filhos (Adolescentes cadastrados)
                </Label>
                <p className="text-xs text-gray-500 mb-2">Selecione os adolescentes que são filhos deste voluntário</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <span className="truncate">
                        {volunteerForm.children_ids?.length > 0 
                          ? `${volunteerForm.children_ids.length} filho(s) selecionado(s)`
                          : 'Nenhum filho vinculado'}
                      </span>
                      <ChevronDown size={16} className="ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96 p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <div className="p-2 border-b sticky top-0 bg-white z-10">
                      <Input 
                        placeholder="Buscar por nome..." 
                        className="h-9"
                        value={childrenSearch}
                        onChange={(e) => setChildrenSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto overscroll-contain p-2" style={{ scrollBehavior: 'smooth' }}>
                      {(() => {
                        const filteredChildren = allAdolescents
                          .filter(a => a.status !== 'inativo')
                          .filter(a => !childrenSearch || a.name.toLowerCase().includes(childrenSearch.toLowerCase()))
                          .sort((a, b) => {
                            // Selecionados primeiro
                            const aSelected = volunteerForm.children_ids?.includes(a.id) ? 0 : 1;
                            const bSelected = volunteerForm.children_ids?.includes(b.id) ? 0 : 1;
                            if (aSelected !== bSelected) return aSelected - bSelected;
                            return a.name.localeCompare(b.name, 'pt-BR');
                          })
                          .slice(0, 100); // Limitar para performance
                        
                        if (filteredChildren.length === 0) {
                          return (
                            <div className="text-center py-4 text-gray-500 text-sm">
                              {childrenSearch ? 'Nenhum adolescente encontrado' : 'Nenhum adolescente disponível'}
                            </div>
                          );
                        }
                        
                        return filteredChildren.map(adolescent => (
                          <label 
                            key={adolescent.id} 
                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={volunteerForm.children_ids?.includes(adolescent.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setVolunteerForm({...volunteerForm, children_ids: [...(volunteerForm.children_ids || []), adolescent.id]});
                                } else {
                                  setVolunteerForm({...volunteerForm, children_ids: (volunteerForm.children_ids || []).filter(id => id !== adolescent.id)});
                                }
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium block truncate">{adolescent.name}</span>
                              {adolescent.phone && (
                                <span className="text-xs text-gray-500">{adolescent.phone}</span>
                              )}
                            </div>
                          </label>
                        ));
                      })()}
                      {allAdolescents.filter(a => a.status !== 'inativo').length > 100 && !childrenSearch && (
                        <p className="text-xs text-gray-400 text-center py-2 border-t mt-2">
                          Digite para buscar mais adolescentes...
                        </p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                {volunteerForm.children_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {volunteerForm.children_ids.map(childId => {
                      const child = allAdolescents.find(a => a.id === childId);
                      return child ? (
                        <span key={childId} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {child.name}
                          <button 
                            type="button"
                            onClick={() => setVolunteerForm({...volunteerForm, children_ids: volunteerForm.children_ids.filter(id => id !== childId)})}
                            className="ml-1 hover:text-blue-600"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setVolunteerDialogOpen(false); resetVolunteerForm(); }}>Cancelar</Button>
              <Button type="submit" className="btn-primary">{editingId ? 'Atualizar' : 'Cadastrar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service Area Dialog */}
      <Dialog open={areaDialogOpen} onOpenChange={(open) => { setAreaDialogOpen(open); if (!open) setAreaForm({ name: '', description: '', category_id: '' }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Área de Serviço</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveArea} className="space-y-4">
            <div>
              <Label>Categoria</Label>
              <Select value={areaForm.category_id || 'none'} onValueChange={(val) => setAreaForm({...areaForm, category_id: val === 'none' ? '' : val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome *</Label>
              <Input value={areaForm.name} onChange={(e) => setAreaForm({...areaForm, name: e.target.value})} required placeholder="Ex: Louvor" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={areaForm.description} onChange={(e) => setAreaForm({...areaForm, description: e.target.value})} placeholder="Ex: Ministério de louvor e adoração" />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setAreaDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-primary">Criar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Volunteers;
